import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { formatDate, formatCurrency } from "@/lib/utils";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CuteLoader } from "@/components/CuteLoader";

export default function ManagerDashboard() {
  const [pendingPTO, setPendingPTO] = useState([]);
  const [allPTO, setAllPTO] = useState([]);
  const [teamMetrics, setTeamMetrics] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const [ptoRes, metricsRes] = await Promise.all([
        api.get("/pto-requests"),
        api.get("/manager/team-metrics")
      ]);
      const pending = ptoRes.data.filter(r => r.status === "pending");
      setPendingPTO(pending);
      setAllPTO(ptoRes.data);
      setTeamMetrics(metricsRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handlePTOAction = async (requestId, status, notes) => {
    try {
      await api.patch(`/pto-requests/${requestId}`, {
        status,
        reviewed_by: "manager",
        notes
      });
      toast.success(`PTO request ${status}`);
      loadData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || `Failed to ${status} request`;
      toast.error(errorMsg);
    }
  };

  if (loading) {
    return <CuteLoader />;
  }

  return (
    <div className="min-h-screen bg-background p-4 md:p-8" data-testid="manager-dashboard">
      <div className="max-w-7xl mx-auto space-y-8">
        <header>
          <h1 className="text-4xl sm:text-5xl font-medium tracking-tight text-foreground">Manager Dashboard</h1>
          <p className="text-base text-muted-foreground mt-2">Team oversight and approvals</p>
        </header>

        <Tabs defaultValue="pto" className="space-y-6" data-testid="manager-tabs">
          <TabsList>
            <TabsTrigger value="pto" data-testid="tab-pto">
              PTO Requests {pendingPTO.length > 0 && `(${pendingPTO.length})`}
            </TabsTrigger>
            <TabsTrigger value="team" data-testid="tab-team">Team Metrics</TabsTrigger>
          </TabsList>

          <TabsContent value="pto">
            <div className="space-y-6">
              {pendingPTO.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-2xl">Pending Approvals</CardTitle>
                    <CardDescription>PTO requests awaiting your review</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {pendingPTO.map((request) => (
                      <PTOApprovalCard 
                        key={request._id || request.id} 
                        request={request} 
                        onAction={handlePTOAction}
                        testId={`pending-pto-${request._id || request.id}`}
                      />
                    ))}
                  </CardContent>
                </Card>
              )}

              <Card>
                <CardHeader>
                  <CardTitle className="text-xl">All PTO Requests</CardTitle>
                  <CardDescription>Complete history</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {allPTO.map((request) => (
                    <Card key={request._id || request.id} data-testid={`pto-history-${request._id}`}>
                      <CardContent className="pt-6">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="font-medium">Employee ID: {request.employee_id}</div>
                            <div className="text-sm text-muted-foreground mt-1">
                              {formatDate(request.start_date)} - {formatDate(request.end_date)}
                            </div>
                            <div className="text-sm text-muted-foreground">{request.hours} hours</div>
                            {request.reason && <div className="text-sm mt-2">{request.reason}</div>}
                          </div>
                          <span className={`inline-flex px-3 py-1 text-xs rounded-full ${
                            request.status === 'approved' ? 'bg-green-100 text-green-800' :
                            request.status === 'denied' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {request.status}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="team">
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Team Performance Metrics</CardTitle>
                <CardDescription>Revenue per clocked hour analysis ($60/hr benchmark)</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <Card className="bg-accent">
                    <CardContent className="pt-6">
                      <div className="text-center">
                        <div className="text-sm font-bold tracking-widest uppercase text-primary mb-2">Team Average</div>
                        <div className="text-4xl font-medium text-foreground">
                          {teamMetrics.team_average ? formatCurrency(teamMetrics.team_average) : "$0.00"}/hr
                        </div>
                        <div className="text-sm text-muted-foreground mt-2">
                          Benchmark: $60/hr
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <div className="space-y-4">
                    {teamMetrics.employees?.map((emp, idx) => (
                      <Card key={idx} data-testid={`team-metric-${idx}`}>
                        <CardContent className="pt-6">
                          <div className="flex justify-between items-center">
                            <div>
                              <div className="font-medium">{emp.name}</div>
                              <div className="text-sm text-muted-foreground mt-1">
                                {emp.hours_worked?.toFixed(1) || 0} hours clocked
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-2xl font-medium text-primary">
                                {formatCurrency(emp.revenue_per_hour || 0)}/hr
                              </div>
                              <div className={`text-sm mt-1 ${
                                (emp.revenue_per_hour || 0) >= 60 ? 'text-green-600' : 'text-muted-foreground'
                              }`}>
                                {(emp.revenue_per_hour || 0) >= 60 ? '✓ Above benchmark' : 'Below benchmark'}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function PTOApprovalCard({ request, onAction, testId }) {
  const [notes, setNotes] = useState("");
  const [showNotes, setShowNotes] = useState(false);

  return (
    <Card data-testid={testId}>
      <CardContent className="pt-6">
        <div className="space-y-4">
          <div>
            <div className="font-medium">Employee ID: {request.employee_id}</div>
            <div className="text-sm text-muted-foreground mt-1">
              {formatDate(request.start_date)} - {formatDate(request.end_date)}
            </div>
            <div className="text-sm text-muted-foreground">{request.hours} hours</div>
            {request.reason && (
              <div className="text-sm mt-2 p-3 bg-accent rounded-md">
                <strong>Reason:</strong> {request.reason}
              </div>
            )}
          </div>
          
          {showNotes && (
            <div className="space-y-2">
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add a note to the employee..."
                data-testid="pto-approval-notes"
              />
            </div>
          )}

          <div className="flex gap-2">
            <Button
              onClick={() => onAction(request._id || request.id, "approved", notes)}
              className="flex-1"
              data-testid="approve-pto-button"
            >
              Approve
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                if (!showNotes) {
                  setShowNotes(true);
                } else {
                  onAction(request._id || request.id, "denied", notes);
                }
              }}
              className="flex-1"
              data-testid="deny-pto-button"
            >
              {showNotes ? "Confirm Denial" : "Deny"}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}